import type { ExerciseTemplate } from '@yuan-math/exercises';
import { gcd } from '@yuan-math/math';
import { choiceExercise, expressionExercise, numericExercise, options, template, trap } from './authoring.ts';

/**
 * Rewriting linear expressions.
 *
 * Three habits account for nearly every wrong answer here, and each has a
 * template that provokes it deliberately:
 *
 *   - the multiplier reaching only the first term inside a bracket,
 *   - the minus in front of a bracket applied to the first term only, and
 *   - a variable term added to a constant, as if 3x + 5 were 8x.
 *
 * The expansion questions ask for a *single coefficient* rather than the whole
 * expression. That is not timidity about expression grading: an equivalence
 * check would accept the unexpanded original as an answer to "expand this",
 * marking a student correct for doing nothing at all.
 */
export const LINEAR_EXPRESSION_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'linear-expand-constant',
    conceptId: 'linear-expressions',
    description: 'Expand a bracket and give the constant term — the term a half-distribution gets wrong.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const k = rng.int(2, difficulty === 3 ? 12 : 7);
      const a = rng.int(2, 9);
      const b = rng.int(2, difficulty === 3 ? 15 : 9);
      const letter = rng.pick(['x', 'n', 'y']);

      return numericExercise({
        prompt: 'Expand this bracket. What is the constant term of your answer?',
        latex: `${k}(${a}${letter} + ${b})`,
        value: k * b,
        summary: `The ${k} multiplies both terms: ${k} × ${a}${letter} = ${k * a}${letter} and ${k} × ${b} = ${k * b}. The constant term is ${k * b}.`,
        hint: 'The number outside the bracket reaches everything inside it, not just the first term.',
        steps: [
          { label: 'Multiply the first term.', latex: `${k} \\times ${a}${letter} = ${k * a}${letter}` },
          { label: 'Multiply the second term.', latex: `${k} \\times ${b} = ${k * b}` },
          { label: 'The expansion.', latex: `${k * a}${letter} + ${k * b}` },
        ],
        traps: [
          // The bracket's second term carried through unmultiplied.
          trap(b, 'distribute-first-term-only'),
          // k added to b rather than multiplied by it.
          ...(k + b === k * b ? [] : [trap(k + b, 'unlike-terms-combined')]),
        ],
      });
    },
  }),

  template({
    id: 'linear-expand-negative',
    conceptId: 'linear-expressions',
    description: 'Simplify a − b(cx + d), where the minus sign has to reach both terms.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(5, difficulty === 4 ? 40 : 20);
      const b = rng.int(2, difficulty === 4 ? 9 : 5);
      const c = rng.int(2, 9);
      const d = rng.int(2, difficulty === 4 ? 12 : 8);
      const constant = a - b * d;

      return numericExercise({
        prompt: 'Simplify this expression. What is its constant term?',
        latex: `${a} - ${b}(${c}x + ${d})`,
        value: constant,
        summary: `The multiplier is −${b}, so the bracket gives −${b * c}x − ${b * d}. The constant term is ${a} − ${b * d} = ${constant}.`,
        hint: `Treat the minus sign as part of the multiplier: the bracket is being multiplied by −${b}, not by ${b}.`,
        steps: [
          { label: `Multiply both terms by −${b}.`, latex: `-${b * c}x - ${b * d}` },
          { label: 'Write the whole expression.', latex: `${a} - ${b * c}x - ${b * d}` },
          { label: 'Collect the constants.', latex: `${a} - ${b * d} = ${constant}` },
        ],
        traps: [
          // The minus applied to the bracket's first term only.
          ...(a + b * d === constant ? [] : [trap(a + b * d, 'negative-bracket-not-distributed')]),
          // Only the visible d subtracted, with the b never distributed at all.
          ...(a - d === constant ? [] : [trap(a - d, 'distribute-first-term-only')]),
        ],
      });
    },
  }),

  template({
    id: 'linear-collect-terms',
    conceptId: 'linear-expressions',
    description: 'Collect like terms, with a constant that must be left alone.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const c = rng.int(2, difficulty === 3 ? 15 : 9);
      const d = rng.int(1, c - 1);
      const letter = rng.pick(['x', 'n', 'y']);
      const coefficient = a + b;
      const constant = c - d;

      return expressionExercise({
        prompt: 'Simplify by collecting like terms.',
        latex: `${a}${letter} + ${c} + ${b}${letter} - ${d}`,
        canonical: `${coefficient}*${letter} + ${constant}`,
        summary: `The ${letter} terms give ${a} + ${b} = ${coefficient}, and the numbers give ${c} − ${d} = ${constant}. The constant cannot join the ${letter} terms.`,
        hint: `${a}${letter} and ${b}${letter} are alike, so they add. A plain number is not alike with either of them.`,
        steps: [
          { label: 'Group what is alike.', latex: `(${a}${letter} + ${b}${letter}) + (${c} - ${d})` },
          { label: `Add the ${letter} terms.`, latex: `${coefficient}${letter}` },
          { label: 'Combine the numbers.', latex: String(constant) },
          { label: 'Put them together — no further step is possible.', latex: `${coefficient}${letter} + ${constant}` },
        ],
        traps: [
          // Everything swept into one variable term.
          trap(`${coefficient + constant}${letter}`, 'unlike-terms-combined'),
        ],
      });
    },
  }),

  template({
    id: 'linear-factor-gcf',
    conceptId: 'linear-expressions',
    description: 'Choose the fully factored form, against one that is only partly factored.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      // The greatest common factor is `gcf`; `partial` is a proper divisor of
      // it, so the distractor is a true statement left unfinished — which is
      // exactly the error worth naming.
      const partial = rng.pick([2, 3]);
      const rest = rng.int(2, difficulty === 4 ? 6 : 4);
      const gcf = partial * rest;
      const a = rng.int(2, 9);
      // Coprime, or `gcf` would not be the greatest common factor after all and
      // the "fully factored" answer would still have work left in it.
      const b = rng.pick([2, 3, 5, 7, 9, 11, 13].filter((n) => n !== a && gcd(n, a) === 1));

      const { choices, correctId } = options(
        [
          { latex: `${gcf}(${a}x + ${b})`, correct: true },
          { latex: `${partial}(${rest * a}x + ${rest * b})`, misconceptionId: 'factor-not-fully-taken-out' },
          { latex: `${gcf}(${a}x + ${gcf * b})`, misconceptionId: 'distribute-first-term-only' },
          { latex: `${gcf}x(${a} + ${b})`, misconceptionId: 'unlike-terms-combined' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which of these is ${gcf * a}x + ${gcf * b} factored completely?`,
        choices,
        correctId,
        summary: `${gcf} is the greatest common factor of ${gcf * a} and ${gcf * b}, and ${a} and ${b} share nothing further. Taking out only ${partial} leaves a factor of ${rest} still inside the bracket.`,
        hint: `Expand each option. Two of them are not even equal to the original — and of the ones that are, only one leaves a bracket with nothing left to take out.`,
        steps: [
          { label: 'Find the greatest common factor.', latex: `\\gcd(${gcf * a}, ${gcf * b}) = ${gcf}` },
          { label: 'Divide each term by it.', latex: `${a}x + ${b}` },
          { label: 'Write it as a product.', latex: `${gcf}(${a}x + ${b})` },
        ],
      });
    },
  }),

  template({
    id: 'linear-equivalent-expression',
    conceptId: 'linear-expressions',
    description: 'Pick the expression equivalent to one with a bracket and a like term outside it.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const k = rng.int(2, difficulty === 4 ? 9 : 5);
      const b = rng.int(2, difficulty === 4 ? 12 : 8);
      // Keeping the outside coefficient below k leaves a positive x term, so
      // the distractors differ from the answer in one visible way at a time.
      const outside = rng.int(1, k - 1);
      const coefficient = k - outside;
      const term = coefficient === 1 ? 'x' : `${coefficient}x`;

      const { choices, correctId } = options(
        [
          { latex: `${term} + ${k * b}`, correct: true },
          { latex: `${term} + ${b}`, misconceptionId: 'distribute-first-term-only' },
          { latex: `${coefficient + k * b}x`, misconceptionId: 'unlike-terms-combined' },
          // The subtraction applied to the constant as well as to the x term.
          { latex: `${term} + ${k * b - outside}`, misconceptionId: 'unlike-terms-combined' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which expression is equivalent to this one?',
        latex: `${k}(x + ${b}) - ${outside === 1 ? 'x' : `${outside}x`}`,
        choices,
        correctId,
        summary: `Expanding gives ${k}x + ${k * b}, and taking ${outside}x away leaves ${term} + ${k * b}. The constant is untouched by the subtraction.`,
        hint: 'Expand the bracket first — both terms — then deal with the term outside it.',
        steps: [
          { label: 'Expand the bracket.', latex: `${k}x + ${k * b}` },
          { label: `Subtract ${outside}x. Only the x terms are alike.`, latex: `${term} + ${k * b}` },
        ],
      });
    },
  }),

  template({
    id: 'linear-write-and-simplify',
    conceptId: 'linear-expressions',
    description: 'Build a linear expression from a situation, then simplify it.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const people = rng.int(2, difficulty === 4 ? 8 : 5);
      const each = rng.int(2, 12);
      const fee = rng.int(1, 9);
      const scenario = rng.pick([
        `${people} friends each buy n tickets at £${each} and pay a booking fee of £${fee} on their own order.`,
        `${people} classes each order n textbooks at £${each} and pay £${fee} postage per class.`,
        `${people} teams each hire n boats at £${each} an hour and pay a deposit of £${fee} per team.`,
      ]);

      return expressionExercise({
        prompt: `${scenario} Write a fully simplified expression for the total paid.`,
        canonical: `${people * each}*n + ${people * fee}`,
        summary: `Each of the ${people} pays ${each}n + ${fee}, so the total is ${people}(${each}n + ${fee}) = ${people * each}n + ${people * fee}.`,
        hint: `Write what one of them pays, then multiply the whole thing by ${people} — every part of it.`,
        steps: [
          { label: 'What one of them pays.', latex: `${each}n + ${fee}` },
          { label: `Multiply by ${people}, reaching both terms.`, latex: `${people}(${each}n + ${fee})` },
          { label: 'Expand.', latex: `${people * each}n + ${people * fee}` },
        ],
        traps: [
          // The fee charged once overall rather than per order.
          ...(fee === people * fee ? [] : [trap(`${people * each}*n + ${fee}`, 'distribute-first-term-only')]),
        ],
      });
    },
  }),
];
